import React, { useState, useEffect, useCallback, useRef } from "react";
import { X, Loader2, ScanLine, Zap, Coffee, ChevronDown, RotateCcw, CheckCircle2 } from "lucide-react";
import { supabase } from "../../utils/supabaseClient";
import { withTimeout } from "../../utils/withTimeout";
import TermHelp from "../common/TermHelp";

// ── Image compression ──────────────────────────────────────────────────────────
// Keep receipt text readable. Dime screenshots are often very tall; limiting the
// longest edge to 600px reduced a 1284×5067 receipt to about 152×600px and made
// OCR guess prices and dates. Bound width and height independently instead.
const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("ไม่สามารถอ่านไฟล์ภาพได้ กรุณาเลือกไฟล์อื่น"));
    reader.onload = (e) => {
      const originalMimeType = file.type || "image/jpeg";
      const canSendOriginal =
        ["image/png", "image/jpeg", "image/webp"].includes(originalMimeType) &&
        file.size <= 3.5 * 1024 * 1024;

      if (canSendOriginal) {
        resolve({
          base64: String(e.target.result).split(",")[1],
          mimeType: originalMimeType,
        });
        return;
      }

      const img = new Image();
      img.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ กรุณาลองอีกครั้ง"));
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 1600;
        const MAX_HEIGHT = 5000;
        const scale = Math.min(
          1,
          MAX_WIDTH / img.naturalWidth,
          MAX_HEIGHT / img.naturalHeight
        );
        canvas.width  = Math.round(img.naturalWidth  * scale);
        canvas.height = Math.round(img.naturalHeight * scale);
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // บังคับ jpeg เสมอ — แก้ปัญหา HEIC/HEIF บน iOS ที่ Gemini ไม่รองรับ
        resolve({
          base64: canvas.toDataURL("image/jpeg", 0.86).split(",")[1],
          mimeType: "image/jpeg",
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

// ── Helpers ────────────────────────────────────────────────────────────────────
const getLocalISOString = () => {
  const d = new Date();
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyForm = () => ({
  id_stock:         "",
  type:             "Buy",
  date_transaction: getLocalISOString(),
  gross_amount:     "",
  amount_stock:     "",
  price:            "",
  fx_rate:          "",
  commission:       "",
  vat:              "",
  sec_fee:          "",
  taf_fee:          "",
  total_amount:     "",
});

const getInputCls = (isDarkMode, extra = "") =>
  `w-full px-3 h-12 rounded-xl border font-bold outline-none text-sm transition-colors ${
    isDarkMode
      ? "bg-slate-900 border-slate-700 focus:border-sky-500"
      : "bg-slate-50 border-slate-200 focus:border-[#132B43]"
  } ${extra}`;

// ── Component ──────────────────────────────────────────────────────────────────
const TransactionForm = ({
  onClose,
  userId,
  onSaveSuccess,
  isDarkMode,
  editingItem,
  callAI,
  transactions,
  onUpgradeClick,
  isPro,
  userRole,
  profile,
  themeConfig,
}) => {
  const isAdmin =
    String(userRole || "").toLowerCase().includes("admin") ||
    String(userRole || "").toLowerCase() === "checker";
  const isUnlimitedAccess = isAdmin || (isPro && profile?.isTrial === false);

  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError]   = useState(null);
  const [isSaving, setIsSaving]     = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [canRetryScan, setCanRetryScan] = useState(false);
  const [wasAiFilled, setWasAiFilled] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const lastScanFile = useRef(null);
  const [quota, setQuota]           = useState({
    count:       0,
    limit:       20,
    isUnlimited: isUnlimitedAccess,
    loading:     true,
  });
  const [formData, setFormData] = useState(emptyForm);

  // ── Populate form when editing ─────────────────────────────────────────────
  useEffect(() => {
    if (!editingItem) return;
    setFormData({
      id_stock:         editingItem.id_stock || editingItem.ID_Stock || editingItem.symbol || "",
      type:             editingItem.type || editingItem.Type || "Buy",
      date_transaction: (
        editingItem.date_transaction ||
        editingItem.Date_Transaction ||
        getLocalISOString()
      ).slice(0, 16),
      gross_amount:  editingItem.gross_amount  ?? editingItem.Gross_Amount  ?? "",
      amount_stock:  editingItem.amount_stock  ?? editingItem.Amount_Stock  ?? editingItem.units ?? "",
      price:         editingItem.price         ?? editingItem.Price         ?? "",
      fx_rate:       editingItem.fx_rate       ?? editingItem.FX_Rate       ?? "",
      commission:    editingItem.commission    ?? editingItem.Commission    ?? "",
      vat:           editingItem.vat           ?? editingItem.VAT           ?? "",
      sec_fee:       editingItem.sec_fee       ?? editingItem.SEC_Fee       ?? "",
      taf_fee:       editingItem.taf_fee       ?? editingItem.TAF_Fee       ?? "",
      total_amount:  editingItem.total_amount  ?? editingItem.Total_Amount  ?? editingItem.net ?? "",
    });
  }, [editingItem]);

  // ── Load quota display (read-only, ไม่นับซ้ำ) ────────────────────────────
  useEffect(() => {
    if (!userId || isUnlimitedAccess) {
      setQuota((q) => ({ ...q, loading: false, isUnlimited: isUnlimitedAccess }));
      return;
    }
    let cancelled = false;
    supabase
      .from("profiles")
      .select("ai_scan_used, last_scan_month")
      .eq("id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const currentMonth = new Date().toISOString().slice(0, 7);
        const count = data?.last_scan_month === currentMonth ? data?.ai_scan_used || 0 : 0;
        setQuota({ count, limit: 20, isUnlimited: false, loading: false });
      })
      .catch(() => {
        if (!cancelled) setQuota((q) => ({ ...q, loading: false, isUnlimited: false }));
      });
    return () => { cancelled = true; };
  }, [userId, isUnlimitedAccess]);

  // ── Sync new symbol to queue ───────────────────────────────────────────────
  const syncSymbolQueue = useCallback((symbol) => {
    if (!symbol) return;
    supabase
      .from("new_stock_queue")
      .upsert(
        { symbol: symbol.toUpperCase().trim(), added_by: userId || null },
        { onConflict: "symbol", ignoreDuplicates: true }
      )
      .then(({ error }) => {
        if (error) console.error("new_stock_queue:", error.message);
      })
      .catch((err) => console.error("new_stock_queue unexpected:", err));
  }, [userId]);

  // ── Scan handler ───────────────────────────────────────────────────────────
  const handleScan = async (source) => {
    const file = source?.target?.files?.[0] || source;
    if (source?.target) source.target.value = "";
    if (!file) return;

    lastScanFile.current = file;
    setScanError(null);
    setCanRetryScan(false);
    setIsScanning(true);

    try {
      // compress ก่อน — ลด size + แปลง HEIC → JPEG
      const { base64, mimeType } = await compressImage(file);

      // Guard: ถ้าหลัง compress ยังใหญ่เกิน 4 MB ให้หยุด
      // (4 MB base64 ≈ 3 MB binary ซึ่งเป็น limit ที่ปลอดภัยสำหรับ Edge Function)
      const approxMB = (base64.length * 0.75) / 1024 / 1024;
      if (approxMB > 4) {
        throw new Error("ไฟล์ภาพมีขนาดใหญ่เกินไป กรุณาเลือกภาพที่เล็กลงแล้วลองอีกครั้ง");
      }

      // ส่ง AI — quota จัดการโดย Edge Function เพียงที่เดียว
      // ไม่ต้องเรียก check_and_increment_ai_quota RPC ที่ client อีกแล้ว
      const res = await callAI(base64, mimeType);
      if (!res) throw new Error("AI Scan อ่านสลิปนี้ไม่สำเร็จ กรุณาใช้ภาพที่ชัดและไม่ถูกตัดขอบ");

      // ถ้า Edge Function ส่ง error QUOTA_EXCEEDED กลับมา
      if (res?.error === "QUOTA_EXCEEDED") {
        setScanError("คุณใช้สิทธิ AI Scan ครบ 20 ครั้งของเดือนนี้แล้ว ยังเพิ่มข้อมูลด้วยตนเองได้ หรือเปิดสิทธิ Supporter เพื่อสแกนไม่จำกัด");
        if (onUpgradeClick) onUpgradeClick();
        return;
      }

      const newSymbol = String(res.ID_Stock || res.id_stock || res.symbol || "").toUpperCase().trim();
      const newGross  = Number(res.Gross_Amount || res.gross_amount || 0).toFixed(2);
      const newUnits  = Number(res.Amount_Stock || res.amount_stock || 0).toFixed(5);

      // ตรวจ duplicate
      const isDuplicate = (transactions || []).some((tx) => {
        const exSymbol = String(tx.ID_Stock || "").toUpperCase().trim();
        return (
          newSymbol === exSymbol &&
          Number(tx.gross_amount || 0).toFixed(2) === newGross &&
          Number(tx.Amount_Stock || 0).toFixed(5) === newUnits
        );
      });
      if (isDuplicate) setScanError("สลิปนี้อาจซ้ำกับรายการเดิม กรุณาตรวจสอบก่อนบันทึกอีกครั้ง");

      // อัปเดต quota display (บวก 1 หลังจาก Edge Function ยืนยัน)
      if (!quota.isUnlimited) {
        const serverCount = Number(res.AI_Quota_Used);
        const serverLimit = Number(res.AI_Quota_Limit) || quota.limit;
        setQuota((q) => ({
          ...q,
          count: Number.isFinite(serverCount) ? serverCount : Math.min(q.count + 1, serverLimit),
          limit: serverLimit,
        }));
      }

      setFormData((prev) => {
        let rawGross      = res.Gross_Amount ?? res.gross_amount ?? res.Gross ?? res.gross;
        const fxRate      = res.FX_Rate || res.fx_rate || "";
        const newPrice    = res.Price || res.price;
        const newAmt      = res.Amount_Stock || res.amount_stock || prev.amount_stock;
        const currency    = res.Currency || res.currency || "";

        if (rawGross !== undefined && Number(fxRate) > 0) {
          const totalAmt = Number(res.Total_Amount || res.total_amount || Number(newPrice) * Number(newAmt));
          const isTHB    = currency.toUpperCase() === "THB" || Number(rawGross) > totalAmt * 5;
          if (isTHB) rawGross = (Number(rawGross) / Number(fxRate)).toFixed(2);
        }

        return {
          ...prev,
          id_stock:         res.ID_Stock || res.id_stock || prev.id_stock,
          type:             res.Type || res.type || prev.type,
          gross_amount:     rawGross ?? prev.gross_amount,
          amount_stock:     newAmt,
          price:            newPrice,
          fx_rate:          fxRate || prev.fx_rate,
          commission:       res.Commission ?? res.commission ?? "",
          vat:              res.VAT ?? res.vat ?? "",
          sec_fee:          res.SEC_Fee ?? res.sec_fee ?? "",
          taf_fee:          res.TAF_Fee ?? res.taf_fee ?? "",
          total_amount:     res.Total_Amount ?? res.total_amount ?? "",
          date_transaction: (res.Date_Transaction || res.date_transaction || prev.date_transaction).slice(0, 16),
        };
      });
      setWasAiFilled(true);
    } catch (err) {
      setScanError(err?.message || "AI Scan ไม่พร้อมใช้งานชั่วคราว กรุณากรอกข้อมูลด้วยตนเองหรือลองอีกครั้ง");
      setCanRetryScan(true);
    } finally {
      setIsScanning(false);
    }
  };

  // ── Save handler ───────────────────────────────────────────────────────────
  const handleSubmit = async (reviewConfirmed = false) => {
    if (isSaving) return;
    const nextErrors = {};
    if (!formData.id_stock.trim()) nextErrors.id_stock = "กรุณากรอกชื่อย่อหุ้น (Symbol)";
    if (!formData.date_transaction) nextErrors.date_transaction = "กรุณาเลือกวันและเวลาของรายการ";
    if (!(Number(formData.gross_amount) > 0)) nextErrors.gross_amount = "กรุณากรอกยอดรายการที่มากกว่า 0";
    if (!(Number(formData.price) > 0)) nextErrors.price = "กรุณากรอกราคาที่มากกว่า 0";
    if (!(Number(formData.amount_stock) > 0)) nextErrors.amount_stock = "กรุณากรอกจำนวนหุ้น";
    if (!(Number(formData.total_amount) > 0)) nextErrors.total_amount = "กรุณากรอกยอดสุทธิตามคำสั่งซื้อขาย";
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      setScanError("กรุณาตรวจช่องที่ทำเครื่องหมายก่อนบันทึก");
      return;
    }
    setFieldErrors({});
    if (wasAiFilled && !reviewConfirmed) {
      setShowReview(true);
      return;
    }
    setShowReview(false);
    
    setIsSaving(true);
    const symbol = formData.id_stock.toUpperCase().trim();

    const payload = {
      user_id: userId,
      id_stock: symbol,
      type: formData.type,
      date_transaction: formData.date_transaction,
      gross_amount: Number(formData.gross_amount) || 0,
      amount_stock: Number(Number(formData.amount_stock).toFixed(7)),
      price: Number(Number(formData.price).toFixed(4)),
      fx_rate: Number(formData.fx_rate) || 0,
      commission: Number(formData.commission) || 0,
      vat: Number(formData.vat) || 0,
      sec_fee: Number(formData.sec_fee) || 0,
      taf_fee: Number(formData.taf_fee) || 0,
      total_amount: Number(formData.total_amount),
      last_updated: new Date().toISOString(),
    };
    if (editingItem?.id) payload.id = editingItem.id;

    try {
      // 1. บันทึกข้อมูลหลักก่อน (ให้จบแค่นี้เพื่อให้ UI ตอบสนองทันที)
      const { data, error } = await supabase.from("transactions").upsert(payload).select().single();
      if (error) throw error;

      // 2. ปิดหน้าจอทันที ไม่ต้องรอ syncSymbolQueue
      onClose(); 
      if (onSaveSuccess && data) onSaveSuccess(data);

      // 3. ปล่อยงานเบื้องหลังทำงานไป (Fire & Forget)
      syncSymbolQueue(symbol);
    } catch (e) {
      console.error("Save Error:", e);
      setScanError("บันทึกรายการไม่สำเร็จ กรุณาตรวจการเชื่อมต่อแล้วลองอีกครั้ง ข้อมูลที่กรอกไว้ยังอยู่ครบ");
      setIsSaving(false);
    }
  };

  const inputCls = (extra = "") => getInputCls(isDarkMode, extra);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className={`xfolyo-entry-form p-5 md:p-7 rounded-xl border max-w-2xl w-full relative max-h-[90vh] overflow-y-auto ${
        isDarkMode
          ? "bg-[#1E1F20] border-slate-700 text-white"
          : "bg-white border-slate-200 text-slate-900"
      }`}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl md:text-2xl font-black">เพิ่มรายการซื้อขาย</h3>
            {!quota.loading && (
              <div
                className={`px-3 py-1.5 rounded-lg text-[11px] md:text-[11px] font-black uppercase tracking-wide flex items-center gap-1.5 shadow-sm border transition-all animate-in fade-in duration-300 ${
                  quota.isUnlimited
                    ? isDarkMode
                      ? "bg-amber-300 text-amber-950 border-amber-200"
                      : "bg-amber-100 text-amber-950 border-amber-300"
                    : quota.count >= quota.limit
                    ? "bg-rose-50 border-rose-200 text-rose-600"
                    : "bg-indigo-50 border-indigo-200 text-indigo-600"
                }`}
              >
                {quota.isUnlimited ? <Coffee size={12} /> : <Zap size={12} />}
                {quota.isUnlimited
                  ? "สแกนไม่จำกัด"
                  : `สิทธิ AI Scan: ${quota.count}/${quota.limit}`}
              </div>
            )}
          </div>
          <p className="text-sm font-medium opacity-70 mt-1">
            ตรวจสอบข้อมูลคำสั่งซื้อขายก่อนบันทึกลงพอร์ต
          </p>
        </div>
        <button type="button" aria-label="ปิดแบบฟอร์มรายการซื้อขาย" onClick={onClose} className="xfolyo-icon-control hover:text-rose-500 mt-1">
          <X />
        </button>
      </div>

      {/* Scan zone */}
      <div
        className={`mb-6 p-4 border-2 border-dashed rounded-2xl text-center relative transition-colors ${
          isScanning
            ? "bg-indigo-500/10 border-indigo-500"
            : scanError
            ? "bg-rose-500/10 border-rose-500"
            : isDarkMode
            ? "border-slate-700 opacity-50 hover:bg-slate-800"
            : "border-slate-300 opacity-70 hover:bg-slate-50"
        }`}
      >
        <input
          type="file"
          accept="image/*"
          className="absolute inset-0 opacity-0 cursor-pointer"
          onChange={handleScan}
          disabled={isScanning}
        />
        <div className="flex flex-col items-center gap-2">
          {isScanning ? (
            <Loader2 className="animate-spin text-indigo-500" />
          ) : (
            <ScanLine className={scanError ? "text-rose-500" : "text-indigo-500"} />
          )}
          <span role={scanError ? "alert" : undefined} className={`text-sm font-semibold ${scanError ? "text-rose-600" : ""}`}>
            {isScanning ? "AI กำลังอ่านข้อมูล..." : scanError || "สแกนสลิป Dime!"}
          </span>
          {canRetryScan && lastScanFile.current && !isScanning && (
            <button type="button" onClick={(event) => { event.stopPropagation(); handleScan(lastScanFile.current); }} className="relative z-10 inline-flex min-h-10 items-center gap-2 rounded-xl border border-rose-300 bg-white px-4 text-sm font-black text-rose-700 hover:bg-rose-50">
              <RotateCcw size={15} aria-hidden="true" /> ลองอ่านภาพเดิมอีกครั้ง
            </button>
          )}
        </div>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 text-left">

        {/* Symbol */}
        <div>
          <label className="xfolyo-field-label">ชื่อย่อหุ้น (Symbol)</label>
          <input
            className={inputCls(fieldErrors.id_stock ? "border-rose-500" : "")}
            aria-invalid={Boolean(fieldErrors.id_stock)}
            value={formData.id_stock}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, id_stock: e.target.value.toUpperCase() }))
            }
          />
          {fieldErrors.id_stock && <p className="xfolyo-field-error">{fieldErrors.id_stock}</p>}
        </div>

        {/* Type */}
        <div>
          <label className="xfolyo-field-label">ประเภท</label>
          <select
            className={`w-full px-3 h-12 rounded-xl border font-black outline-none text-sm ${
              formData.type === "Buy"
                ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                : "bg-rose-500/10 text-rose-600 border-rose-500/20"
            }`}
            value={formData.type}
            onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value }))}
          >
            <option value="Buy">ซื้อ (Buy)</option>
            <option value="Sell">ขาย (Sell)</option>
          </select>
        </div>

        {/* Date */}
        <div>
          <label className="xfolyo-field-label">วันและเวลา</label>
          <input
            type="datetime-local"
            className={inputCls(fieldErrors.date_transaction ? "border-rose-500" : "")}
            aria-invalid={Boolean(fieldErrors.date_transaction)}
            value={formData.date_transaction}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, date_transaction: e.target.value }))
            }
          />
          {fieldErrors.date_transaction && <p className="xfolyo-field-error">{fieldErrors.date_transaction}</p>}
        </div>

        {/* Gross Amount */}
        <div>
          <label className="xfolyo-field-label text-indigo-600">
            ยอดรายการรวม (USD)
          </label>
          <input
            type="number" step="any" placeholder="ยอดตามคำสั่งซื้อขาย"
            className={inputCls(`${isDarkMode ? "" : "bg-indigo-50/50"} ${fieldErrors.gross_amount ? "border-rose-500" : ""}`)}
            aria-invalid={Boolean(fieldErrors.gross_amount)}
            value={formData.gross_amount}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, gross_amount: e.target.value }))
            }
          />
          {fieldErrors.gross_amount && <p className="xfolyo-field-error">{fieldErrors.gross_amount}</p>}
        </div>

        {/* Price */}
        <div>
          <label className="xfolyo-field-label">ราคาที่ได้จริง (USD)</label>
          <input
            type="number" step="any"
            className={inputCls(fieldErrors.price ? "border-rose-500" : "")}
            aria-invalid={Boolean(fieldErrors.price)}
            value={formData.price}
            onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value }))}
          />
          {fieldErrors.price && <p className="xfolyo-field-error">{fieldErrors.price}</p>}
        </div>

        {/* Shares */}
        <div>
          <label className="xfolyo-field-label text-emerald-600">
            จำนวนหุ้น
          </label>
          <input
            type="number" step="any"
            aria-invalid={Boolean(fieldErrors.amount_stock)}
            className={`w-full px-3 h-12 rounded-xl border font-bold outline-none text-sm ${fieldErrors.amount_stock ? "border-rose-500" : ""} ${
              isDarkMode
                ? "bg-emerald-900/20 border-emerald-500/30 text-emerald-400"
                : "bg-emerald-50 border-emerald-200 text-emerald-600"
            }`}
            value={formData.amount_stock}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, amount_stock: e.target.value }))
            }
          />
          {fieldErrors.amount_stock && <p className="xfolyo-field-error">{fieldErrors.amount_stock}</p>}
        </div>

        {/* FX Rate */}
        <div>
          <label className="xfolyo-field-label text-amber-600 inline-flex items-center gap-1.5">
            อัตราแลกเปลี่ยน (USD/THB)
            <TermHelp term="FX Rate">จำนวนเงินบาทต่อ 1 ดอลลาร์สหรัฐที่ใช้จริงในรายการนี้ มีผลต่อต้นทุนและกำไรเมื่อแปลงเป็นเงินบาท</TermHelp>
          </label>
          <input
            type="number" step="any" placeholder="เช่น 34.50"
            className={`w-full px-3 h-12 rounded-xl border font-bold outline-none text-sm ${
              isDarkMode
                ? "bg-amber-900/20 border-amber-500/30 text-amber-400"
                : "bg-amber-50 border-amber-200 text-amber-700"
            }`}
            value={formData.fx_rate}
            onChange={(e) => setFormData((prev) => ({ ...prev, fx_rate: e.target.value }))}
          />
        </div>

        {/* Fees section */}
        <button type="button" className="col-span-2 md:col-span-3 xfolyo-disclosure" aria-expanded={showAdvanced} onClick={() => setShowAdvanced((value) => !value)}>
          <span><strong>ค่าธรรมเนียมและค่าใช้จ่าย</strong><small>กรอกเมื่อมีข้อมูลในใบยืนยันจากโบรกเกอร์</small></span>
          <ChevronDown size={18} className={showAdvanced ? "rotate-180" : ""} aria-hidden="true" />
        </button>

        {showAdvanced && ["commission", "vat", "sec_fee", "taf_fee"].map((field) => (
          <div key={field}>
            <label className="xfolyo-field-label">
              {field.replace("_", " ")}
            </label>
            <input
              type="number" step="any"
              className={inputCls()}
              value={formData[field]}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, [field]: e.target.value }))
              }
            />
          </div>
        ))}

        {/* Net Total */}
        <div>
          <label className="xfolyo-field-label text-sky-600">
            ยอดสุทธิ (USD)
          </label>
          <input
            type="number" step="any"
            aria-invalid={Boolean(fieldErrors.total_amount)}
            className={`w-full px-3 h-12 rounded-xl border font-black outline-none text-sm ${fieldErrors.total_amount ? "border-rose-500" : ""} ${
              isDarkMode
                ? "bg-slate-900 border-slate-700 text-sky-400"
                : "bg-sky-50 border-sky-200 text-[#132B43]"
            }`}
            value={formData.total_amount}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, total_amount: e.target.value }))
            }
          />
          {fieldErrors.total_amount && <p className="xfolyo-field-error">{fieldErrors.total_amount}</p>}
        </div>

        {showReview && (
          <section className={`col-span-2 md:col-span-3 rounded-2xl border p-4 md:p-5 ${isDarkMode ? "border-emerald-700 bg-emerald-950/30" : "border-emerald-200 bg-emerald-50"}`} aria-label="ตรวจข้อมูลที่ AI อ่านก่อนบันทึก" aria-live="polite">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-600" size={22} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <h4 className="font-black">ตรวจข้อมูลจาก AI ก่อนบันทึก</h4>
                <p className="mt-1 text-sm opacity-70">เปรียบเทียบกับสลิปอีกครั้ง โดยเฉพาะราคา จำนวนหุ้น ยอดสุทธิ และอัตราแลกเปลี่ยน</p>
              </div>
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm md:grid-cols-4">
              {[
                ["หุ้น", formData.id_stock || "—"],
                ["ประเภท", formData.type === "Buy" ? "ซื้อ" : "ขาย"],
                ["ยอดรวม", `$${formData.gross_amount || "—"}`],
                ["ราคา", `$${formData.price || "—"}`],
                ["จำนวนหุ้น", formData.amount_stock || "—"],
                ["USD/THB", formData.fx_rate || "—"],
                ["ค่าธรรมเนียมรวม", `$${["commission", "vat", "sec_fee", "taf_fee"].reduce((sum, key) => sum + Number(formData[key] || 0), 0).toFixed(4)}`],
                ["ยอดสุทธิ", `$${formData.total_amount || "—"}`],
              ].map(([label, value]) => <div key={label}><dt className="text-xs font-bold opacity-60">{label}</dt><dd className="mt-1 break-words font-mono font-black">{value}</dd></div>)}
            </dl>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setShowReview(false)} className="min-h-11 rounded-xl border border-current/15 px-5 text-sm font-bold">กลับไปแก้ไข</button>
              <button type="button" onClick={() => handleSubmit(true)} disabled={isSaving} className="min-h-11 rounded-xl bg-emerald-700 px-5 text-sm font-black text-white hover:bg-emerald-800 disabled:opacity-50">ยืนยันและบันทึก</button>
            </div>
          </section>
        )}

        {/* Actions */}
        <div className="col-span-2 md:col-span-3 flex gap-4 pt-4 border-t border-slate-800/10 mt-2">
          <button
            type="button" onClick={onClose}
            className={`flex-1 p-4 h-12 rounded-xl font-bold uppercase text-xs ${
              isDarkMode
                ? "bg-slate-800 text-slate-400 hover:bg-slate-700"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            ยกเลิก
          </button>
          <button
            type="button" onClick={() => handleSubmit(false)} disabled={isSaving}
            className={`flex-1 p-4 h-12 rounded-xl text-white font-bold uppercase text-xs shadow-lg disabled:opacity-50 transition-colors ${
              isDarkMode
                ? "bg-sky-500 hover:bg-sky-600 shadow-sky-500/20"
                : "bg-[#132B43] hover:bg-[#132B43]/90 shadow-slate-900/20"
            }`}
          >
            {isSaving ? "กำลังบันทึก..." : wasAiFilled ? "ตรวจข้อมูลก่อนบันทึก" : "บันทึกรายการ"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TransactionForm);
